import { BladeController, View } from "@tweakpane/core";
import { Renderer } from "ogl";
import type { OGLRenderingContext } from "ogl";
import { BladeApi, Pane } from "tweakpane";

export interface BaseShaderConfig {
  [key: string]: any;
}

export interface BaseShader {
  updateParams(params: Partial<BaseShaderConfig>): void;
  reset(): void;
  exportConfig(): BaseShaderConfig;
  destroy(): void;
  getParams(): BaseShaderConfig;
}

export interface ShaderDefinition<
  TConfig extends BaseShaderConfig,
  TShader extends BaseShader
> {
  create(
    canvas: HTMLCanvasElement,
    config?: Partial<TConfig>
  ): Promise<TShader>;
  attachDevtools?(shader: TShader): Promise<DevtoolsInstance>;
}

export interface DevtoolsInstance {
  pane: Pane;
  fpsGraph?: BladeApi<BladeController<View>>;
  dispose(): void;
}

export interface WebGLSetup {
  renderer: Renderer;
  gl: OGLRenderingContext;
}

/**
 * Validates canvas parameter and WebGL support for shader creation.
 * This function should be called by all shader implementations before creating WebGL contexts.
 *
 * @param canvas - The canvas element to validate
 * @param shaderName - Name of the shader (for error messages)
 * @throws Error if canvas is invalid or WebGL is not supported
 */
export function validateCanvas(
  canvas: HTMLCanvasElement,
  shaderName: string = "Shader"
): void {
  if (!canvas) {
    throw new Error(
      `${shaderName}: Canvas parameter is required and cannot be null or undefined`
    );
  }

  if (!(canvas instanceof HTMLCanvasElement)) {
    throw new Error(
      `${shaderName}: Canvas parameter must be an HTMLCanvasElement`
    );
  }

  if (!canvas.isConnected) {
    throw new Error(
      `${shaderName}: Canvas must be attached to the DOM before creating shader`
    );
  }

  if (!canvas.clientWidth || !canvas.clientHeight) {
    throw new Error(
      `${shaderName}: Canvas must have non-zero dimensions (width and height)`
    );
  }

  if (typeof window !== "undefined") {
    const tempCanvas = document.createElement("canvas");
    let testContext: WebGLRenderingContext | null = null;

    try {
      testContext = (tempCanvas.getContext("webgl2") ||
        tempCanvas.getContext("webgl") ||
        tempCanvas.getContext(
          "experimental-webgl"
        )) as WebGLRenderingContext | null;
    } catch (error) {
      throw new Error(
        `${shaderName}: Failed to get WebGL context - WebGL may not be supported`
      );
    }

    if (!testContext) {
      throw new Error(`${shaderName}: WebGL is not supported in this browser`);
    }

    tempCanvas.remove();
  }
}

/**
 * Creates a validated WebGL setup for shader creation.
 * This function validates the canvas, creates the OGL renderer, and returns a ready-to-use WebGL setup.
 *
 * @param canvas - The canvas element to use
 * @param shaderName - Name of the shader (for error messages)
 * @returns WebGL setup with renderer and context
 * @throws Error if canvas is invalid, WebGL is not supported, or renderer creation fails
 */
export function createWebGLSetup(
  canvas: HTMLCanvasElement,
  shaderName: string = "Shader"
): WebGLSetup {
  validateCanvas(canvas, shaderName);

  let renderer: Renderer;
  let gl: OGLRenderingContext;

  try {
    renderer = new Renderer({
      dpr: devicePixelRatio,
      canvas,
      width: canvas.clientWidth,
      height: canvas.clientHeight,
    });
    gl = renderer.gl;
  } catch (error) {
    throw new Error(
      `${shaderName}: Failed to create WebGL renderer - ${
        error instanceof Error ? error.message : "Unknown error"
      }`
    );
  }

  if (!gl) {
    throw new Error(`${shaderName}: Failed to get WebGL context from renderer`);
  }

  gl.clearColor(1, 1, 1, 0);

  return { renderer, gl };
}
